define('presentation/welcome/viewModels/onboardingAvailabilityViewModel',[
    'i18next',
    'businessServices/country/timeZones',
    'common/collections/enumerationKeyValueConverter',
    'common/promises/promiseFactory',
    'constants/dayOfWeekEnumeration',
    'constants/liveReceptionistScriptFollowUpConstants',
    'presentation/welcome/common/onboardingNavigationManager',
    'presentation/welcome/common/onboardingSectionValidationDisplay',
    'presentation/welcome/facades/onboardingAvailabilityFacade',
    'presentation/welcome/validators/onboardingAvailabilityValidator',
    'presentation/welcome/viewModels/weekdayScheduleViewModel'
], function() {
    return function() {
        let self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const TimeZoneProviderConstructor = require('businessServices/country/timeZones');
        const _timeZoneProvider = new TimeZoneProviderConstructor();

        const WeekdayScheduleViewModelConstructor = require('presentation/welcome/viewModels/weekdayScheduleViewModel');

        const SectionValidationDisplayConstructor = require('presentation/welcome/common/onboardingSectionValidationDisplay');
        const _sectionValidationDisplay = new SectionValidationDisplayConstructor();

        const _i18n = require('i18next');
        const _dayOfWeekEnumeration = require('constants/dayOfWeekEnumeration');
        const _followUpConstants = require('constants/liveReceptionistScriptFollowUpConstants');
        const _keyValueConverter = require('common/collections/enumerationKeyValueConverter');

        let _facade = null;
        let _disposables = [];
        let _onboardingNavigationManager = null;
        let _validator = null;
        let _pendingBusinessHoursReset = null;

        const _weekdays = _keyValueConverter.convertToKeyValues(_dayOfWeekEnumeration);
        const _operatingHoursSchedule = [
            _dayOfWeekEnumeration.monday,
            _dayOfWeekEnumeration.tuesday,
            _dayOfWeekEnumeration.wednesday,
            _dayOfWeekEnumeration.thursday,
            _dayOfWeekEnumeration.friday,
            _dayOfWeekEnumeration.saturday,
            _dayOfWeekEnumeration.sunday
        ];

        self.isScrollToOffsetActive = true;
        self.isCompositionComplete = ko.observable(false);
        self.showValidation = ko.observable(false);
        self.isScrollActive = ko.observable(false);

        self.headingText = _i18n.t('onboardingAvailability:headingText');
        self.subtitleText = _i18n.t('onboardingAvailability:subtitleText');

        self.isValid = ko.observable(false);

        self.timeZone = ko.observable('');
        self.operatingHours = ko.observableArray([]);
        self.callerFollowUp = ko.observable();
        self.callerFollowUpOther = ko.observable('').extend({ rateLimit: 500 });
        self.shouldDisplayOperatingHours = ko.observable(false);

        self.selectPlaceHolderMessage = ko.pureComputed(() => _i18n.t("onboardingAvailability:select"));
        self.showOperatingHoursTooltip = ko.pureComputed(() => {
            if (self.shouldDisplayOperatingHours()) {
                return _i18n.t("onboardingAvailability:regularOperatingHoursTooltip");
            }
            return _i18n.t("onboardingAvailability:flexibleOperatingHoursTooltip");
        });

        self.isNavigateToNextPageActive = ko.pureComputed(() => {
            if (self.isValid() !== true) {
                return false;
            }
            if (self.shouldDisplayOperatingHours() === false) {
                return true;
            }
            return self.operatingHours().every((weekdaySchedule) => weekdaySchedule.openTime.isValid() !== false);
        });

        const _availableTimezones = _timeZoneProvider.getSortedTimeZones();
        self.timeZones = ko.pureComputed(() => {
            return _availableTimezones.map((timeZone) => {
                timeZone.id = timeZone.key;
                timeZone.text = timeZone.name;
                return timeZone;
            });
        });

        self.callerFollowUpOptions = [
            {id: _followUpConstants.SameDay, text: _i18n.t('onboardingAvailability:sameDay')},
            {id: _followUpConstants.WithinTwoDays, text: _i18n.t('onboardingAvailability:withinTwoDays')},
            {id: _followUpConstants.Other, text: _i18n.t('onboardingAvailability:other')}
        ];

        self.showCallerFollowUpOther = ko.pureComputed(() => {
            return self.callerFollowUp() && self.callerFollowUp() === _followUpConstants.Other;
        });

        _sectionValidationDisplay.init(self.showValidation);
        self.canDisplayCallerFollowUpOtherValidation = _sectionValidationDisplay.createSectionDisplay(self.showCallerFollowUpOther);
        self.canDisplayOperatingHoursValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayOperatingHours);

        self.onForwardArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);
                        _sectionValidationDisplay.captureDisplayedSections();

                        deferredObject.resolve(isValid);
                    });
            });
        };

        const _handleCallerFollowUpChanged = () => {
            if (self.callerFollowUp()) {
                _facade.saveCallerFollowUp(self.callerFollowUp());
            }
            self.callerFollowUpOther.validate();
        };

        const _handleCallerFollowUpOtherChanged = () => {
            if (self.callerFollowUpOther()) {
                _facade.saveCallerFollowUpOther(self.callerFollowUpOther());
            }
        };

        const _handleWeekdayScheduleChanged = (dayOfWeek) => {
            const schedule = self.operatingHours().find((schedule) => schedule.dayOfWeek() === dayOfWeek);
            const openTime = schedule.openTime2400();
            const closeTime = schedule.closeTime2400();
            _facade.saveBusinessHours(dayOfWeek, openTime, closeTime);
        };

        const _handleTimeZoneChanged = (timeZone) => {
            if (self.timeZone()){
                _facade.saveTimeZone(timeZone);
            }
        };

        const _revalidateOperatingHours = () => {
            self.operatingHours().forEach((weekdaySchedule) => weekdaySchedule.openTime.validate());
        };

        const _persistDisplayedOperatingHours = () => {
            self.operatingHours().forEach((schedule) => {
                _facade.saveBusinessHours(schedule.dayOfWeek(), schedule.openTime2400(), schedule.closeTime2400());
            });
        };

        const _handleShouldDisplayOperatingHoursChanged = (shouldDisplay) => {
            if (!shouldDisplay) {
                _pendingBusinessHoursReset = _facade.resetBusinessHours();
                _revalidateOperatingHours();
                return;
            }

            const allDaysEmpty = self.operatingHours().every((schedule) => {
                return !schedule.openTime() && !schedule.closeTime();
            });

            if (allDaysEmpty) {
                const weekdayDays = [
                    _dayOfWeekEnumeration.monday,
                    _dayOfWeekEnumeration.tuesday,
                    _dayOfWeekEnumeration.wednesday,
                    _dayOfWeekEnumeration.thursday,
                    _dayOfWeekEnumeration.friday
                ];

                self.operatingHours().forEach((schedule) => {
                    if (weekdayDays.includes(schedule.dayOfWeek())) {
                        schedule.openTime('09:00 am');
                        schedule.closeTime('06:00 pm');
                        schedule.isOpen(true);
                    }
                });
            }

            if (_pendingBusinessHoursReset !== null) {
                const pendingReset = _pendingBusinessHoursReset;
                _pendingBusinessHoursReset = null;
                pendingReset
                    .fail(_persistDisplayedOperatingHours)
                    .done(_persistDisplayedOperatingHours);
            } else {
                _persistDisplayedOperatingHours();
            }

            _revalidateOperatingHours();
        };

        const _subscribeToWeekdayObservables = (weekday) => {
            const dayOfWeek = weekday.dayOfWeek();
            _disposables.push(weekday.openTime.subscribe(() => _handleWeekdayScheduleChanged(dayOfWeek)));
            _disposables.push(weekday.closeTime.subscribe(() => _handleWeekdayScheduleChanged(dayOfWeek)));
            _disposables.push(weekday.closeTime.subscribe(() => weekday.openTime.validate()));
            _disposables.push(weekday.isOpen.subscribe(() => weekday.openTime.validate()));
        };

        const _populateHoursOfOperation = (onboardingSession) => {
            let isFlexibleHours = true;

            _operatingHoursSchedule.forEach((day) => {
                const weekday = _weekdays.find((weekday) => weekday.value === day).key;
                const weekdayOpenTime = onboardingSession[`${weekday}OpenTime`];
                const weekdayCloseTime = onboardingSession[`${weekday}CloseTime`];

                if (weekdayOpenTime !== null || weekdayCloseTime !== null) {
                    isFlexibleHours = false;
                }

                const schedule = new WeekdayScheduleViewModelConstructor(weekdayOpenTime, weekdayCloseTime);
                schedule.dayOfWeek(day);
                self.operatingHours.push(schedule);

                _subscribeToWeekdayObservables(schedule);
            });

            self.shouldDisplayOperatingHours(!isFlexibleHours);
        };

        const _seedValidation = () => {
            self.timeZone.validate();
            self.callerFollowUp.validate();
            self.callerFollowUpOther.validate();
            _revalidateOperatingHours();
        };

        self.detached = () => {
            _onboardingNavigationManager.detached();
            _sectionValidationDisplay.detached();
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/welcome/facades/onboardingAvailabilityFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const OnboardingNavigationManagerConstructor = require('presentation/welcome/common/onboardingNavigationManager');
            _onboardingNavigationManager = new OnboardingNavigationManagerConstructor();
            _onboardingNavigationManager.init();

            const ValidatorConstructor = require('presentation/welcome/validators/onboardingAvailabilityValidator');
            _validator = new ValidatorConstructor();

            return _initialize();
        };

        const _initialize = () => {
            _validator.registerViewModel(self, _facade);

            _promiseFactory.deferIndefinitely(deferredObject => {
                _facade.getLiveReceptionistOnboarding()
                    .fail(deferredObject.reject)
                    .done((onboardingSession) => {
                        self.timeZone(onboardingSession.timeZone);

                        if (onboardingSession.callerFollowUp && onboardingSession.callerFollowUp.toString() !== _followUpConstants.Unknown) {
                            self.callerFollowUp(onboardingSession.callerFollowUp.toString());
                        }
                        if (onboardingSession.callerFollowUpOther) {
                            self.callerFollowUpOther(onboardingSession.callerFollowUpOther);
                        }

                        _populateHoursOfOperation(onboardingSession);
                        _disposables.push(
                            self.timeZone.subscribe(_handleTimeZoneChanged),
                            self.callerFollowUp.subscribe(_handleCallerFollowUpChanged),
                            self.callerFollowUpOther.subscribe(_handleCallerFollowUpOtherChanged),
                            self.shouldDisplayOperatingHours.subscribe(_handleShouldDisplayOperatingHoursChanged)
                        );
                        _seedValidation();
                        deferredObject.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});

